"use client";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useState } from "react";
import { AlertOctagonIcon, AlertTriangleIcon, CheckCircleIcon, CrossIcon, InfoIcon } from "../../icons/index.js";
import renderIcon from "../../icons/render-icon.js";
import makeStyles from "../../theming/make-styles.js";
import { IconButton } from "../button/index.js";
import CircularProgress from "../circular-progress/index.js";
import useInterval from "../utils/use-interval.js";
import { useNotification } from "./notification-provider.js";
const useStyles = makeStyles()((theme, { color, shadow, block, autogrow }, classes) => {
    const palette = theme.palette(color).notification;
    return {
        notification: Object.assign(Object.assign({ alignSelf: "flex-end", width: block ? "100%" : autogrow ? undefined : theme.size(272), minWidth: autogrow ? undefined : theme.size(272), padding: theme.spacing("md", "md", "md", "lg"), backgroundColor: palette.backgroundColor, borderLeft: `${theme.size(4)} solid ${palette.header.color}`, borderRadius: theme.size(4), overflowWrap: "anywhere", pointerEvents: "all" }, (!shadow
            ? {
                "position": "relative",
                "overflow": "hidden",
                "&::after": {
                    content: "''",
                    position: "absolute",
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    border: `${theme.size(1)} solid ${palette.borderColor}`,
                    borderLeftWidth: 0,
                    borderTopRightRadius: "inherit",
                    borderBottomRightRadius: "inherit",
                    pointerEvents: "none",
                },
            }
            : {
                boxShadow: theme.shadow("md"),
            })), { [`& + .${classes.notification}`]: {
                marginTop: theme.spacing(12),
            } }),
        close: {
            "position": "relative",
            "alignSelf": "flex-start",
            "justifySelf": "flex-end",
            "& button": {
                position: "relative",
                zIndex: 1,
            },
            "& > svg": {
                "position": "absolute",
                "transform": "rotateY(-180deg)",
                "& [class^='progress']": {
                    transitionDuration: "0",
                },
            },
        },
        content: {
            [`.${classes.header} + &`]: {
                marginTop: theme.spacing("sm"),
            },
            "& ul": {
                paddingLeft: theme.spacing("lg"),
                margin: 0,
            },
        },
        header: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontSize: theme.typography.small.fontSize,
            color: palette.header.color,
        },
        icon: {
            flexShrink: 0,
            marginRight: theme.spacing("sm"),
            color: palette.header.color,
        },
        title: {
            display: "inline-flex",
            marginRight: theme.spacing("md"),
            fontWeight: theme.typography.fontWeights.medium,
        },
    };
});
const icons = {
    primary: InfoIcon,
    warning: AlertTriangleIcon,
    success: CheckCircleIcon,
    error: AlertOctagonIcon,
};
export const DefaultCloseButton = React.forwardRef(function DefaultCloseButton(_a, ref) {
    var { className, closable, color, id, timeout } = _a, props = __rest(_a, ["className", "closable", "color", "id", "timeout"]);
    const [percent, setPercent] = useState(100);
    const { removeNotification } = useNotification();
    useInterval(() => {
        const newPercent = percent - 1;
        if (id && newPercent === 0) {
            removeNotification(id);
        }
        setPercent(newPercent);
    }, !timeout || percent === 0 ? null : timeout / 100);
    return (closable && (_jsxs("div", { className: className, children: [timeout && (_jsx(CircularProgress, { animate: false, color: color, height: 32, indeterminate: false, strokeWidth: 1.5, transparent: true, value: percent, width: 32 })), _jsx(IconButton, Object.assign({ ref: ref, color: "gray", icon: CrossIcon, round: true, size: "small", variant: "link" }, props))] })));
});
export const Notification = React.forwardRef(function Notification({ autogrow = false, block, children, className, closable = false, closeButton: CloseButton = DefaultCloseButton, color = "primary", footer, icon = icons[color] || null, id, shadow = false, timeout, title, onClose, }, ref) {
    const { removeNotification } = useNotification();
    const { classes, cx } = useStyles({ autogrow, block, color, shadow });
    const handleClose = useCallback((e) => {
        onClose === null || onClose === void 0 ? void 0 : onClose(e);
        id && removeNotification(id);
    }, [id, onClose, removeNotification]);
    return (_jsxs("div", { ref: ref, className: cx(classes.notification, className), "data-component": "notification", role: "alert", children: [_jsxs("div", { className: classes.header, children: [_jsxs("div", { className: classes.title, children: [renderIcon(icon, classes.icon), title] }), _jsx(CloseButton, { className: classes.close, closable: closable, color: color, id: id, timeout: timeout, onClick: handleClose })] }), children && _jsx("div", { className: cx(classes.content, "line-clamp"), children: children }), footer] }));
});
export default Notification;
//# sourceMappingURL=notification.js.map